import axios from 'axios'
import { getTokens } from './jwtStorage'
import { refreshAccessToken } from './jwtRefresh'
import { ApiError } from 'api/errors'

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const AUTH_REFRESH_EXCLUDED_PATHS = [
  '/auth/login',
  '/auth/respond-to-mfa',
  '/auth/refresh-token',
  '/auth/signup',
  '/auth/confirm-signup',
  '/auth/resend-code',
  '/auth/forgot-password',
  '/auth/reset-password'
]

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_GOLDIE_REST_ENDPOINT
})

axiosInstance.defaults.headers.common['X-Client'] = 'goldie-web/1.5.0'

axiosInstance.interceptors.request.use(config => {
  const tokens = getTokens()
  if (tokens) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`
  }
  const method = (config.method ?? '').toLowerCase()
  if (WRITE_METHODS.has(method) && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = (
      crypto as Crypto & { randomUUID(): string }
    ).randomUUID()
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (v: unknown) => void
  reject: (e: unknown) => void
}> = []

const processQueue = (error: unknown) => {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(null)))
  failedQueue = []
}

const shouldAttemptRefresh = (url?: string): boolean => {
  const tokens = getTokens()
  if (tokens === null) return false

  const requestUrl = url ?? ''
  return !AUTH_REFRESH_EXCLUDED_PATHS.some(path => requestUrl.includes(path))
}

axiosInstance.interceptors.response.use(
  response => {
    // Unwrap serverless envelope { status: boolean, data: T } so callers
    // receive the payload directly, matching the old Rails response shape.
    const d = response.data
    if (d !== null && typeof d === 'object' && 'status' in d) {
      const envelope = d as {
        status: boolean
        message?: string
        code?: string
        data?: { code?: string } | unknown
      }
      // The backend returns HTTP 200 with { status: false } for domain
      // failures (e.g. trading paused, expired review order). Without this the
      // envelope would unwrap to `data` (often null) and resolve as a SUCCESS,
      // firing onSuccess with null and showing the success toast on a failed
      // buy/sell. Reject so callers' onError/catch fire instead.
      if (envelope.status === false) {
        // Machine-readable error code: prefer the top-level `code`, fall back to
        // the legacy `data.code` (the Cognito auth router nests it there).
        const inner = envelope.data
        const nestedCode =
          inner !== null && typeof inner === 'object' && 'code' in inner
            ? (inner as { code?: string }).code
            : undefined
        const code = envelope.code ?? nestedCode ?? 'API_ERROR'
        return Promise.reject(
          new ApiError(
            envelope.message ?? 'Request failed',
            code,
            response.status,
            inner
          )
        )
      }
      if ('data' in envelope) {
        response.data = envelope.data
      }
    }
    return response
  },
  async error => {
    const originalRequest = error.config
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      shouldAttemptRefresh(originalRequest.url)
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => axiosInstance(originalRequest))
      }
      originalRequest._retry = true
      isRefreshing = true
      try {
        await refreshAccessToken()
        processQueue(null)
        return axiosInstance(originalRequest)
      } catch (err) {
        processQueue(err)
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }
    return Promise.reject(error)
  }
)

export default axiosInstance
